import { useCallback, useMemo } from 'react';

import { useQuery } from 'react-query';

import usersApi from 'api/rest/users/users';
import { FEATURE_FLAGS, type FeatureConfig, type FeatureFlagKey } from 'constants/dictionaries/featureFlags';
import QUERY from 'constants/queryKeys';

type UseFeatureFlagsResult = {
  featureConfig: FeatureConfig;
  isLoading: boolean;
  isEnabled: (flag: FeatureFlagKey) => boolean;
  refetch: () => void;
};

const emptyConfig: FeatureConfig = {};

/**
 * Reads feature toggles from the cached `GET_ME` response (`feature_config` → `featureConfig`).
 * Unknown or missing flags are treated as disabled (`false`).
 * When `superuser` is enabled, every flag is treated as enabled.
 */
const useFeatureFlags = (): UseFeatureFlagsResult => {
  const { data, isLoading, refetch } = useQuery(QUERY.GET_ME, usersApi.getMe());

  const featureConfig = useMemo(() => {
    const config = data?.featureConfig || emptyConfig;
    return config;
  }, [data?.featureConfig]);

  const isEnabled = useCallback(
    (flag: FeatureFlagKey): boolean => {
      if (featureConfig[FEATURE_FLAGS.SUPERUSER] === true) return true;
      return featureConfig[flag] === true;
    },
    [featureConfig],
  );

  return {
    featureConfig,
    isLoading,
    isEnabled,
    refetch,
  };
};

export const useFeatureFlag = (flag: FeatureFlagKey): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flag);
};

export default useFeatureFlags;
